import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Tables, TablesInsert } from '../../../database.types';
import crypto from 'crypto';

type Brand = Tables<'brands'>;

export const useBrandData = (brandId?: string) => {
  const [currentBrand, setCurrentBrand] = useState<Brand | null>(null);
  const [fieldMetadata, setFieldMetadata] = useState<any>(null);
  const [readyBrands, setReadyBrands] = useState<Brand[]>([]);

  const fetchBrandDetails = useCallback(async (id: string) => {
    if (!id) return null;
    try {
      const { data, error } = await supabase
        .from('brands')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (data) {
        setCurrentBrand(data);
        return data;
      }
    } catch (error) {
      console.error('Error fetching brand details:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    if (brandId) {
      fetchBrandDetails(brandId);
    }
  }, [brandId, fetchBrandDetails]);

  const fetchFieldMetadata = useCallback(async () => {
    const response = await fetch('../config/fieldMetadata.json');
    const data = await response.json();
    setFieldMetadata(data);
  }, []);

  const updateBrandField = useCallback(async (brandId: string, field: string, value: any) => {
    try {
      const { data, error } = await supabase
        .from('brands')
        .update({ [field]: value })
        .eq('id', brandId)
        .single();
      if (error) throw error;
      if (currentBrand && currentBrand.id === brandId) {
        setCurrentBrand({ ...currentBrand, [field]: value });
      }
      return data;
    } catch (error) {
      console.error('Error updating brand field:', error);
      throw error;
    }
  }, [currentBrand]);

  const generateAIContent = useCallback(async (
    brandId: string,
    fieldName: string,
    additionalInstructions: string,
    existingValue?: string,
    feedback?: string
  ) => {
    try {
      console.log('Function parameters:', { brandId, fieldName, additionalInstructions, existingValue, feedback });

      const workerUrl = import.meta.env.VITE_WORKERS_URL;

      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        throw new Error('No session found');
      }

      // Create the payload
      const payload: any = { brandId, fieldName, additionalInstructions };

      // Add optional parameters if provided
      if (existingValue) {
        payload.existingValue = existingValue;
      }

      if (feedback) {
        payload.feedback = feedback;
      }

      const response = await fetch(`${workerUrl}/rainfactory/brand-genius/generate_missing_field`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.data.session.access_token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      // Check if there was an error from the API
      if (!response.ok) {
        // Create an error object with additional properties
        const error = new Error(data.error || 'Failed to generate content');
        // Add a property to indicate if this is a provider outage
        Object.assign(error, {
          isProviderOutage: data.isProviderOutage,
          status: response.status
        });
        throw error;
      }

      return data.result.generatedContent;
    } catch (error) {
      console.error('Error generating AI content:', error);
      throw error;
    }
  }, []);

  const fetchBrandProducts = useCallback(async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('brand_id', id);
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching brand products:', error);
      throw error;
    }
  }, []);

  const fetchBrandCampaigns = useCallback(async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('brand_id', id);
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching brand campaigns:', error);
      throw error;
    }
  }, []);

  const fetchBrandConvergences = useCallback(async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('convergences')
        .select('*')
        .eq('brand_id', id);
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching brand convergences:', error);
      throw error;
    }
  }, []);

  const createBrand = useCallback(async (brand: Partial<Brand>) => {
    try {
      const brandWithRequiredFields: TablesInsert<'brands'> = {
        ...brand,
        brand_name: brand.brand_name || 'Untitled Brand',
        idea: brand.idea || 'New Brand Idea',
        url: brand.url || '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        id: crypto.randomUUID()
      };

      const { data, error } = await supabase
        .from('brands')
        .insert(brandWithRequiredFields)
        .single();
      if (error) throw error;
      setCurrentBrand(data);
      return data;
    } catch (error) {
      console.error('Error creating brand:', error);
      throw error;
    }
  }, [currentBrand]);

  const fetchAllBrands = useCallback(async () => {
    try {
      // Get current user first
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // Fetch brands the user has access to through their account
      const { data: accountBrands, error: accountError } = await supabase
        .from('brands')
        .select('*')
        .neq('status', 'DELETED')
        .eq('status', 'ACTIVE')  // Only show ACTIVE brands
        .order('brand_name');

      if (accountError) throw accountError;

      // Fetch brands the user has direct access to
      const { data: directAccessBrands, error: directError } = await supabase
        .from('brand_access')
        .select(`
          access_level,
          expires_at,
          brand:brands!inner(
            id,
            brand_name,
            account_id,
            status
          )
        `)
        .eq('user_id', user.id)
        .or('expires_at.is.null,expires_at.gt.' + new Date().toISOString());

      if (directError) throw directError;

      // Combine both sets of brands
      const brandMap = new Map();

      // Add account brands (already filtered for ACTIVE status)
      accountBrands?.forEach(brand => {
        brandMap.set(brand.id, brand);
      });

      // Add or update with direct access brands (filter for ACTIVE status)
      directAccessBrands?.forEach(access => {
        const brand = access.brand;
        // Only include brands that are ACTIVE
        if (brand.status === 'ACTIVE' && !brandMap.has(brand.id)) {
          // This is a brand from another account
          brandMap.set(brand.id, brand);
        }
      });

      const brandsData = Array.from(brandMap.values());

      const validBrands = brandsData.filter(brand =>
        brand.brand_name && brand.brand_name.trim() !== '' && brand.status === 'ACTIVE'
      ).sort((a, b) => a.brand_name.localeCompare(b.brand_name));

      console.log('Filtered brands for sidebar:', validBrands.map(b => ({ id: b.id, name: b.brand_name, status: b.status })));
      setReadyBrands(validBrands);

      return validBrands;
    } catch (error) {
      console.error('Error fetching brands:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    fetchAllBrands();
  }, [fetchAllBrands]);

  const updateBrand = useCallback(async (updates: Partial<Brand>) => {
    if (!currentBrand?.id) return null;

    try {
      const { data, error } = await supabase
        .from('brands')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentBrand.id)
        .select()
        .single();

      if (error) throw error;
      setCurrentBrand(data);
      return data;
    } catch (error) {
      console.error('Error updating brand:', error);
      throw error;
    }
  }, [currentBrand]);

  return {
    brand: currentBrand,
    loading: brandId ? !currentBrand : false,
    setCurrentBrand,
    readyBrands,
    fieldMetadata,
    fetchFieldMetadata,
    updateBrandField,
    updateBrand,
    generateAIContent,
    fetchBrandDetails,
    fetchBrandProducts,
    fetchBrandCampaigns,
    fetchBrandConvergences,
    createBrand,
    fetchAllBrands,
  };
};
